import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { OrganizationService } from '../../organization.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ErrorResponse } from '../../../../core/models/error.models';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';

import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { CommonService } from '../../../../shared/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { UploadEvent } from '../../../../core/services/upload-progress.service';
import { UploadProgressComponent } from '../../../../components/upload-progress/upload-progress.component';

@Component({
  selector: 'app-import-one-roster-file',
  standalone: true,
  imports: [
    NzFormItemComponent,
    FormsModule,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzDropDownModule,
    NzSelectModule,
    NzUploadModule,
    SvgIconComponent,
    NzButtonModule,
    NzNotificationModule,
    ReactiveFormsModule,
    NzModalModule,
    NzAlertModule,
    CommonModule,
    UploadProgressComponent,
  ],
  templateUrl: './import-one-roster-file.component.html',
  styleUrl: './import-one-roster-file.component.scss',
})
export class ImportOneRosterFileComponent {
  uploadForm: FormGroup;
  organizationFile: NzUploadFile[] = [];
  userFile: NzUploadFile[] = [];

  private readonly progressSubject = new BehaviorSubject<number>(0);
  uploadProgress$ = this.progressSubject.asObservable();

  loading$: Observable<boolean>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly organizationService: OrganizationService,
    private readonly message: NzMessageService,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService
  ) {
    this.uploadForm = this.fb.group({
      organizationFile: [null, Validators.required],
      userFile: [null, Validators.required],
    });
    this.loading$ = this.loaderService.loading$;
  }

  beforeOrganizationUpload = (file: NzUploadFile): boolean => {
    return this.handleFileUpload(file, 'organization');
  };

  beforeUserUpload = (file: NzUploadFile): boolean => {
    return this.handleFileUpload(file, 'user');
  };

  private handleFileUpload(
    file: NzUploadFile,
    fileType: 'organization' | 'user'
  ): boolean {
    const isCsv = file.type === 'text/csv';

    if (!isCsv) {
      this.message.error('You can only upload CSV files');
      return false;
    }

    if (fileType === 'organization') {
      this.organizationFile = [file];
      this.uploadForm.patchValue({ organizationFile: file });
    } else {
      this.userFile = [file];
      this.uploadForm.patchValue({ userFile: file });
    }

    return (file.size ?? 0) < 0;
  }

  handleFileRemove = (fileType: 'user' | 'organization') => {
    return (file: NzUploadFile): boolean => {
      if (fileType === 'user') {
        this.userFile = this.userFile.filter(item => item !== file);
      } else {
        this.organizationFile = this.organizationFile.filter(
          item => item !== file
        );
      }
      return true;
    };
  };

  submitForm(): void {
    if (
      !this.uploadForm.valid ||
      !this.organizationFile?.length ||
      !this.userFile?.length
    ) {
      return;
    }

    const orgFile = this.organizationFile[0] as unknown as File;
    const usrFile = this.userFile[0] as unknown as File;

    this.loaderService.show();

    this.organizationService
      .uploadRoster(orgFile, usrFile)
      .pipe(
        tap((event: UploadEvent<void>) => {
          if (event.status === 'progress') {
            this.progressSubject.next(event.percentage);
          } else if (event.status === 'completed') {
            this.progressSubject.next(100);
          }
        }),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (event: UploadEvent<void>) => {
          if (event.status === 'completed') {
            this.commonService.notify('success', 'File uploaded successfully.');
            this.closeImportDrawer();
            this.resetForm();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          if (message === 'Request Timeout') {
            this.progressSubject.next(80);
          } else {
            this.progressSubject.next(0);
          }

          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private resetForm(): void {
    this.uploadForm.reset();
    this.organizationFile = [];
    this.userFile = [];
  }

  closeImportDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.IMPORT);
  }
}
